import { useMemo, useRef, useState } from "react";
import BaseApplicationComponent from "../../../shared/baseComponent/baseWrappers/BaseApplicationComponent";
import { useApiClient } from "../../../shared/customHooks";
import { applicationOutcomeForm } from "../../../shared/formObjects/application/applicationOutcomeForm";
import { useApplicationContext } from "../context/applicationContext";
import { Modal, ModalRef } from "../../modal/modal";
import { ToastInfo } from "../../toaster/toaster";
import { eventEmitter } from "../../../shared/event";
import { useNavigate } from "react-router";
import { FormikProps } from "formik";
import clearHiddenFields from "../../../shared/utils/clearHiddenFields";
import handleResponseError from "../../../shared/utils/handleResponseError";
import { renderHyperlinkMessage } from "../../../shared/CommonFunctions";
import { InputTextarea } from "primereact/inputtextarea";

interface ApplicationOutcomeState {
  currentData: any;
  f: FormikProps<any> | null;
  emailMessages?: any[];
  enableCcInput: boolean;
  emailCcs: string;
  validationError: string;
}

export default function ApplicationOutcomeComponent({ disabled, data }: any) {
  const api = useApiClient("ApplicationOutcome");
  const modalRef = useRef<ModalRef | null>(null);
  const navigate = useNavigate();
  const emailRegex = new RegExp(/^[A-Za-z0-9._*%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}(?:\s*,\s*[A-Za-z0-9._*%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})*$/);

  const [state, setState] = useState<ApplicationOutcomeState>({
    currentData: null,
    f: null,
    emailMessages: undefined,
    enableCcInput: false,
    emailCcs: "",
    validationError: "",
  });

  const { id, application } = useApplicationContext();
  const formContext = useMemo(
    () => ({
      isAppeal: application?.isAppeal,
      isApplicationEligible: application?.applicationBase?.supportType?.isApplicationEligible,
    }),
    [application]
  );

  function getInitialValues(initialData: any, formik: FormikProps<any>) {
    setState((prev) => ({ ...prev, f: formik }));
    formik.setFieldValue("rowVersion", initialData.rowVersion);
  }

  function triggerModal(data: any) {
    data = clearHiddenFields(data);
    setState((prev) => ({ ...prev, currentData: data, emailCcs: "", validationError: "" }));
    state?.f?.validateForm().then((value) => {
      if (JSON.stringify(value) === "{}") {
        api
          .postData(data, `preview/${id}`)
          .then((res) => {
            setState((prev) => ({
              ...prev,
              enableCcInput: res?.data?.record?.enableCcInput ?? false,
              emailMessages: res?.data?.record?.emailMessages ?? [],
            }));
            modalRef.current?.toggle();
          })
          .catch((error: any) => {
            handleResponseError(error);
          });
      } else {
        state?.f?.setTouched(touchAllFields(value));
      }
    });
  }

  async function sendEmailAsync() {
    api
      .postData({ ...state.currentData, emailCcs: state.emailCcs }, `SubmitApplicationOutcome/${id}`)
      .then(() => {
        const data: ToastInfo = { title: "Success", message: "Email Submission Successful!", severity: "success" };
        eventEmitter.emit("tstr", data);
        const targetPath = `/applications/ApplicationBaseCurrent/${id}`;
        navigate(targetPath);
      })
      .catch((error: any) => {
        handleResponseError(error);
      });
  }

  function touchAllFields(obj: any): any {
    if (typeof obj !== "object" || obj === null) {
      return true;
    }
    const touched: any = {};
    for (const key of Object.keys(obj)) {
      touched[key] = touchAllFields(obj[key]);
    }
    return touched;
  }

  function validateEmailCcs(emailCcs: string) {
    const emailCcsTrimmed = emailCcs.trim();
    if (emailCcsTrimmed.length === 0) {
      setState((prev) => ({ ...prev, validationError: "A response is required", emailCcs: "" }));
    } else if (emailRegex.test(emailCcsTrimmed) === false) {
      setState((prev) => ({ ...prev, validationError: "Email CCs must be valid addresses separated by a comma", emailCcs: "" }));
    } else {
      setState((prev) => ({ ...prev, validationError: "", emailCcs: emailCcsTrimmed }));
    }
  }

  return (
    <>
      <BaseApplicationComponent
        disabled={disabled}
        data={data}
        headerLabel={"Application and Appeal Outcome"}
        editPath="applicationOutcome"
        apiClient={api}
        formBody={applicationOutcomeForm}
        formContext={formContext}
        id={id}
        showEdit={disabled}
        print={true}
        customSubmit={triggerModal}
        getInitialValues={getInitialValues}
        showNoValidateBtn={false}
      />
      <Modal
        ref={modalRef}
        confirmAction={sendEmailAsync}
        cancelAction={() => {
          setState((prev) => ({ ...prev, emailMessages: [], emailCcs: "", validationError: "" }));
        }}
        disableSubmit={state.enableCcInput ? state.validationError.length > 0 || state.emailCcs.length === 0 : false}
        headerLabel="Submit & Send Outcome Email"
        confirmLabel="Submit and send email">
        <div className="email-table">
          {state.enableCcInput && (
            <div>
              <p className="mb-2">Enter the C.C. Email Address/s for the email*</p>
              <InputTextarea
                id="emailCcs"
                className="w-full email-cc-textarea"
                onChange={(e) => {
                  validateEmailCcs(e.target.value);
                }}
                rows={1}
                autoResize
              />
              {state.validationError && <p className="error mb-1">{state.validationError}</p>}
              <p className="fieldadv mb-2">
                If there are any additional people or organisation who need to receive this email (e.g. KIS Provider, EYM), enter all of the email
                addresses in this field separated by a comma
              </p>
            </div>
          )}
          <p>If you submit this form, the system will update the status of this application and send an email to the kindergarten advising:</p>
          <br />
          {state.emailMessages !== undefined &&
            state.emailMessages.map((x: { message: any; links: any }, index: number) => (
              <div key={index} style={{ whiteSpace: "pre-wrap" }}>
                <div>{renderHyperlinkMessage(x.message, x.links)}</div>
                <br />
              </div>
            ))}
        </div>
      </Modal>
    </>
  );
}
